import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FaAt, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import { userService } from '../../services/apiService';
import { getActiveMentionQuery, insertMention } from '../../utils/mentions';
import './MentionTextarea.css';

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name || '?').slice(0, 2).toUpperCase();
}

export default function MentionTextarea({
  value,
  onChange,
  maxLength,
  placeholder,
  className = '',
  disabled = false,
  rows = 4,
  hint = true,
}) {
  const textareaRef = useRef(null);
  const listRef = useRef(null);
  const searchTimerRef = useRef(null);
  const requestRef = useRef(0);

  const [cursorPos, setCursorPos] = useState(0);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [open, setOpen] = useState(false);

  const syncMentionState = useCallback((text, pos) => {
    const active = getActiveMentionQuery(text, pos);
    setMentionQuery(active);
    setOpen(Boolean(active));
    setActiveIndex(0);
  }, []);

  const updateCursor = useCallback(() => {
    const pos = textareaRef.current?.selectionStart ?? 0;
    setCursorPos(pos);
    syncMentionState(value, pos);
  }, [syncMentionState, value]);

  useEffect(() => {
    if (!open || !mentionQuery) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    const query = mentionQuery.query;
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);

    if (query.length < 1) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    searchTimerRef.current = window.setTimeout(async () => {
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      setLoading(true);

      try {
        const res = await userService.searchUsers(query, 6);
        if (requestRef.current !== requestId) return;
        setResults(res.data.users || []);
      } catch {
        if (requestRef.current !== requestId) return;
        setResults([]);
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    }, 220);

    return () => {
      if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    };
  }, [mentionQuery, open]);

  const applyMention = useCallback((username) => {
    const pos = textareaRef.current?.selectionStart ?? cursorPos;
    const next = insertMention(value, username, pos);
    onChange(next.text);
    setOpen(false);
    setMentionQuery(null);
    setResults([]);

    window.requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(next.cursorPos, next.cursorPos);
      setCursorPos(next.cursorPos);
    });
  }, [cursorPos, onChange, value]);

  const handleChange = (event) => {
    const nextValue = event.target.value.slice(0, maxLength);
    onChange(nextValue);
    const pos = event.target.selectionStart ?? nextValue.length;
    setCursorPos(pos);
    syncMentionState(nextValue, pos);
  };

  const handleKeyDown = (event) => {
    if (!open || results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + results.length) % results.length);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      applyMention(results[activeIndex].username);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!listRef.current) return;
    const activeItem = listRef.current.querySelector('.mention-suggestion.is-active');
    activeItem?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const showDropdown = open && mentionQuery && mentionQuery.query.length >= 1;

  return (
    <div className="mention-textarea-wrap">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onClick={updateCursor}
        onKeyUp={updateCursor}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={rows}
        className={className}
        disabled={disabled}
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-haspopup="listbox"
      />

      {showDropdown && (
        <div className="mention-suggestions" role="listbox" ref={listRef}>
          <div className="mention-suggestions-head">
            <FaAt aria-hidden="true" />
            <span>Mencionar usuário</span>
            {loading && <FaSpinner className="social-spin" aria-hidden="true" />}
          </div>

          {!loading && results.length === 0 && (
            <p className="mention-suggestions-empty">Nenhum usuário encontrado.</p>
          )}

          <ul>
            {results.map((person, index) => {
              const name = person.profile?.displayName || person.username;
              return (
                <li key={person.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    className={`mention-suggestion${index === activeIndex ? ' is-active' : ''}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyMention(person.username)}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <span className="mention-suggestion-avatar" aria-hidden="true">
                      {person.avatarUrl
                        ? <img src={person.avatarUrl} alt="" />
                        : getInitials(name)}
                    </span>
                    <span className="mention-suggestion-copy">
                      <strong>
                        {name}
                        {person.verified && <FaCheckCircle className="post-verified" />}
                      </strong>
                      <small>@{person.username}</small>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {hint ? (
        <p className="mention-textarea-hint">
          <FaAt aria-hidden="true" />
          Digite <strong>@usuário</strong> para mencionar alguém
        </p>
      ) : null}
    </div>
  );
}
